import { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import './App.css';
import { AuthProvider } from './auth/AuthContext';
import { ProtectedRoute } from './auth/components/ProtectedRoute';
import { ConfirmProvider } from './components/modals/ConfirmDialog';
import { InvitationsProvider } from './components/notifications/InvitationsProvider';
import { ToastProvider, useToast } from './components/notifications/ToastContainer';
import { resolveBadge } from './types/calendarBadges';
import { PageLoader } from './components/PageLoader';
import { isMigrationNeeded, migrateProgressData } from './utils/progressMigration';

// Lazy load ALL pages (code splitting for better performance)
// Only load pages when user navigates to them
// Note: All these are NAMED exports, so we re-export as default
const LoginPage = lazy(() => import('./pages/LoginPage').then(m => ({ default: m.LoginPage })));
const Dashboard = lazy(() => import('./components/dashboard/Dashboard').then(m => ({ default: m.Dashboard })));
const KeyboardSelectionPage = lazy(() => import('./pages/KeyboardSelectionPage').then(m => ({ default: m.KeyboardSelectionPage })));
const KeyboardsManagementPage = lazy(() => import('./pages/KeyboardsManagementPage').then(m => ({ default: m.KeyboardsManagementPage })));
const LanguageSelectionPage = lazy(() => import('./pages/LanguageSelectionPage').then(m => ({ default: m.LanguageSelectionPage })));
const LevelSelectionPage = lazy(() => import('./pages/LevelSelectionPage').then(m => ({ default: m.LevelSelectionPage })));
const ProfilePage = lazy(() => import('./pages/ProfilePageRedesigned').then(m => ({ default: m.ProfilePageRedesigned })));
const SettingsPage = lazy(() => import('./pages/SettingsPage').then(m => ({ default: m.SettingsPage })));
const StorePage = lazy(() => import('./pages/StorePage').then(m => ({ default: m.StorePage })));
const CoursesPage = lazy(() => import('./pages/CoursesPage').then(m => ({ default: m.CoursesPage })));
const DailyChallengePage = lazy(() => import('./pages/DailyChallengePage').then(m => ({ default: m.DailyChallengePage })));
const JustPlayHomePage = lazy(() => import('./pages/JustPlayHomePage').then(m => ({ default: m.JustPlayHomePage })));
const JustPlayPracticePage = lazy(() => import('./pages/JustPlayPracticePage').then(m => ({ default: m.JustPlayPracticePage })));
const JustPlayHistoryPage = lazy(() => import('./pages/JustPlayHistoryPage').then(m => ({ default: m.JustPlayHistoryPage })));
const PaperWizardPage = lazy(() => import('./pages/PaperWizardPage').then(m => ({ default: m.PaperWizardPage })));
const LibraryPage = lazy(() => import('./pages/LibraryPage').then(m => ({ default: m.LibraryPage })));
const TournamentHomePage = lazy(() => import('./pages/TournamentHomePage').then(m => ({ default: m.TournamentHomePage })));
const TournamentLobbyPage = lazy(() => import('./pages/TournamentLobbyPage').then(m => ({ default: m.TournamentLobbyPage })));
const TournamentPlayPage = lazy(() => import('./pages/TournamentPlayPage').then(m => ({ default: m.TournamentPlayPage })));
const TournamentPerformancePage = lazy(() => import('./pages/TournamentPerformancePage').then(m => ({ default: m.TournamentPerformancePage })));
const GroupLeaderboardPage = lazy(() => import('./pages/GroupLeaderboardPage').then(m => ({ default: m.GroupLeaderboardPage })));
const FriendsAndGroupsPage = lazy(() => import('./pages/FriendsAndGroupsPage').then(m => ({ default: m.FriendsAndGroupsPage })));
const WeeklyContestPage = lazy(() => import('./pages/WeeklyContestPage').then(m => ({ default: m.WeeklyContestPage })));
const LevelsDemo = lazy(() => import('./pages/LevelsDemo').then(m => ({ default: m.LevelsDemo })));
const BasicsPage = lazy(() => import('./pages/BasicsPage').then(m => ({ default: m.BasicsPage })));
const PythonPage = lazy(() => import('./pages/PythonPage').then(m => ({ default: m.PythonPage })));
const ClassLevelsPage = lazy(() => import('./pages/ClassLevelsPage').then(m => ({ default: m.ClassLevelsPage })));
const PracticeLevelV2 = lazy(() => import('./pages/PracticeLevelV2').then(m => ({ default: m.PracticeLevelV2 })));
const AdminPage = lazy(() => import('./pages/AdminPage').then(m => ({ default: m.AdminPage })));

function DataClearedListener() {
  const { showToast } = useToast();
  useEffect(() => {
    const handleDataCleared = (event: CustomEvent) => {
      const { clearedProgress, clearedKeyboards, clearedProfile } = event.detail;
      const clearedItems: string[] = [];

      if (clearedProgress) clearedItems.push('training progress');
      if (clearedKeyboards) clearedItems.push('keyboards');
      if (clearedProfile) clearedItems.push('profile');

      if (clearedItems.length > 0) {
        showToast({
          type: 'warning',
          title: 'Data removed by administrator',
          message: `Your ${clearedItems.join(', ')} ha${clearedItems.length === 1 ? 's' : 've'} been removed. The page will refresh in a moment.`,
          duration: 4000,
        });

        // Refresh the page to clear any cached state
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      }
    };

    window.addEventListener('dataCleared', handleDataCleared as EventListener);

    return () => {
      window.removeEventListener('dataCleared', handleDataCleared as EventListener);
    };
  }, [showToast]);
  return null;
}

function ChallengeCompletedListener() {
  const { showToast } = useToast();
  useEffect(() => {
    const handler = (event: CustomEvent) => {
      const newly = (event.detail?.newly ?? []) as Array<{ badgeId?: string; title: string }>;
      for (const n of newly) {
        const badge = resolveBadge(n.badgeId);
        showToast({
          type: 'success',
          title: badge
            ? `🏅 Badge unlocked: ${badge.emoji} ${badge.name}`
            : `✅ Challenge complete: ${n.title}`,
          message: 'Reward added — see Challenges.',
        });
      }
    };
    window.addEventListener('challengesCompleted', handler as EventListener);
    return () => window.removeEventListener('challengesCompleted', handler as EventListener);
  }, [showToast]);
  return null;
}

function App() {
  // Run progress data migration on app startup
  useEffect(() => {
    if (isMigrationNeeded()) {
      migrateProgressData();
    }
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <ConfirmProvider>
          <DataClearedListener />
          <ChallengeCompletedListener />
          <InvitationsProvider>
          <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
          
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/select-keyboard"
            element={
              <ProtectedRoute>
                <KeyboardSelectionPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/keyboards"
            element={
              <ProtectedRoute>
                <KeyboardsManagementPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/select-language"
            element={
              <ProtectedRoute>
                <LanguageSelectionPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/levels"
            element={
              <ProtectedRoute>
                <LevelSelectionPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/settings"
            element={
              <ProtectedRoute>
                <SettingsPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/store"
            element={
              <ProtectedRoute>
                <StorePage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/courses"
            element={
              <ProtectedRoute>
                <CoursesPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/daily-challenge"
            element={
              <ProtectedRoute>
                <DailyChallengePage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/just-play"
            element={
              <ProtectedRoute>
                <JustPlayHomePage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/just-play/practice"
            element={
              <ProtectedRoute>
                <JustPlayPracticePage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/just-play/history"
            element={
              <ProtectedRoute>
                <JustPlayHistoryPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/just-play/wizard"
            element={
              <ProtectedRoute>
                <PaperWizardPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/just-play/library"
            element={
              <ProtectedRoute>
                <LibraryPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/friends-and-groups"
            element={
              <ProtectedRoute>
                <FriendsAndGroupsPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/tournament"
            element={
              <ProtectedRoute>
                <TournamentHomePage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/tournament/lobby/:lobbyId"
            element={
              <ProtectedRoute>
                <TournamentLobbyPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/tournament/play/:tournamentId"
            element={
              <ProtectedRoute>
                <TournamentPlayPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/tournament/performance"
            element={
              <ProtectedRoute>
                <TournamentPerformancePage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/groups/:groupId/leaderboard"
            element={
              <ProtectedRoute>
                <GroupLeaderboardPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/weekly-contest"
            element={
              <ProtectedRoute>
                <WeeklyContestPage />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/demo"
            element={
              <ProtectedRoute>
                <LevelsDemo />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/basics"
            element={
              <ProtectedRoute>
                <BasicsPage progressKey="english" />
              </ProtectedRoute>
            }
          />

          <Route
            path="/basics/class/:classId"
            element={
              <ProtectedRoute>
                <ClassLevelsPage progressKey="english" />
              </ProtectedRoute>
            }
          />

          {/* English routes (alias for basics) */}
          <Route
            path="/english"
            element={
              <ProtectedRoute>
                <BasicsPage progressKey="english" />
              </ProtectedRoute>
            }
          />

          <Route
            path="/english/class/:classId"
            element={
              <ProtectedRoute>
                <ClassLevelsPage progressKey="english" />
              </ProtectedRoute>
            }
          />

          {/* Hebrew course (parallel to English Basics) */}
          <Route
            path="/hebrew"
            element={
              <ProtectedRoute>
                <BasicsPage progressKey="hebrew" />
              </ProtectedRoute>
            }
          />

          <Route
            path="/hebrew/class/:classId"
            element={
              <ProtectedRoute>
                <ClassLevelsPage progressKey="hebrew" />
              </ProtectedRoute>
            }
          />

          {/* Russian course (parallel to English Basics and Hebrew) */}
          <Route
            path="/russian"
            element={
              <ProtectedRoute>
                <BasicsPage progressKey="russian" />
              </ProtectedRoute>
            }
          />

          <Route
            path="/russian/class/:classId"
            element={
              <ProtectedRoute>
                <ClassLevelsPage progressKey="russian" />
              </ProtectedRoute>
            }
          />

          <Route
            path="/python"
            element={
              <ProtectedRoute>
                <PythonPage />
              </ProtectedRoute>
            }
          />

          <Route
            path="/python/class/:classId"
            element={
              <ProtectedRoute>
                <ClassLevelsPage progressKey="python" />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/practice-v2"
            element={
              <ProtectedRoute>
                <PracticeLevelV2 />
              </ProtectedRoute>
            }
          />
          
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminPage />
              </ProtectedRoute>
            }
          />
          
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
        </InvitationsProvider>
        </ConfirmProvider>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
